const REDACTED = "[redacted]";

function redactTokenFields(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(redactTokenFields);
  }
  if (!value || typeof value !== "object") {
    return value;
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, child]) => [
      key,
      key.toLowerCase() === "token" ? REDACTED : redactTokenFields(child),
    ]),
  );
}

/**
 * Keeps invitation tokens out of telemetry. The raw token rides in the
 * `/provider-invite?token=…` URL (harmless on its own — acceptance still
 * requires a logged-in account whose email matches the invite, and the invite
 * is consumed once accepted), but we don't want it landing in Sentry/PostHog
 * URLs or tRPC `input` payloads either, so scrub it from anything telemetry
 * reports.
 */
export function redactInviteTokensFromUrl(value: string): string {
  if (typeof window === "undefined") {
    return value;
  }

  try {
    const isAbsolute = /^[a-z][a-z\d+.-]*:/i.test(value);
    const url = new URL(value, window.location.origin);

    if (url.pathname === "/provider-invite" && url.searchParams.has("token")) {
      url.searchParams.set("token", REDACTED);
    }

    for (const [key, parameter] of [...url.searchParams.entries()]) {
      if (key === "redirect" && parameter.includes("/provider-invite")) {
        url.searchParams.set(key, redactInviteTokensFromUrl(parameter));
      }
      if (key === "input") {
        try {
          url.searchParams.set(
            key,
            JSON.stringify(redactTokenFields(JSON.parse(parameter))),
          );
        } catch {
          // Leave malformed/non-JSON input unchanged.
        }
      }
    }

    return isAbsolute
      ? url.toString()
      : `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return value;
  }
}
